// src/pages/products/NovaPage.jsx
import { motion } from "framer-motion";
import novaIcon from "../../assets/portfolio/coming-soon.png";


export default function NovaPage() {
  return (
    <div className="w-full bg-transparent text-black pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-20">

        {/* ================= HERO ================= */}
        <section className="flex flex-col items-center gap-10 text-center">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="hero-heading mb-4">
              Project Nova
            </h1>

            <p className="hero-copy">
              A collaborative product management and whiteboarding platform
              where teams plan, build, track, and preserve the entire journey
              of a digital product, from idea to delivery.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            <img
              src={novaIcon}
              alt="Project Nova"
              className="
                w-64 h-64 object-contain
                drop-shadow-[0_25px_35px_rgba(0,0,0,0.15)]
              "
            />
          </motion.div>

        </section>

        {/* ================= PRODUCT OVERVIEW ================= */}
        <section className="mx-auto mt-32 max-w-5xl text-center">
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="section-heading mb-6"
          >
            Product Overview
          </motion.h2>

          <p className="section-copy">
            Project Nova was designed to solve a common problem faced by modern
            product teams: fragmented tools, lost context, and disconnected
            conversations across the development lifecycle.
          </p>

          <p className="section-copy mt-6">
            Nova brings product managers, developers, QA teams, clients, and
            stakeholders into a single shared workspace where ideas, decisions,
            discussions, and versions live together — clearly and permanently.
          </p>
        </section>

        {/* ================= OUR STRATEGY ================= */}
        <section className="mx-auto mt-32 max-w-5xl text-center">
          <h2 className="section-heading mb-6">
            Our Strategy
          </h2>

          <p className="section-copy">
            The core strategy behind Project Nova was to eliminate scattered
            workflows. Instead of switching between task managers, whiteboards,
            documents, and chat tools, Nova centralizes planning and execution
            into a unified product space.
          </p>

          <p className="section-copy mt-6">
            Every decision, iteration, and version is stored as part of the
            product’s history — making it easier to understand why something
            was built, not just how it was built.
          </p>
        </section>

        {/* ================= WHAT WE BUILT ================= */}
        <section className="mt-32">
          <h2 className="section-heading mb-10">
            What We Built
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              "Project & task management system",
            "Real time collaborative whiteboarding",
              "Product roadmap & milestone tracking",
              "Version history & change logs",
              "Centralized feedback & discussions",
              "Stakeholder friendly product views",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="
                  glass-card rounded-[22px] p-6
                "
              >
                <h3 className="text-[15px] font-medium">{item}</h3>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ================= WHO IT’S FOR ================= */}
        <section className="mx-auto mt-32 max-w-6xl text-center">
          <h2 className="section-heading mb-10">
            Who Project Nova Is For
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              "Product managers planning roadmaps",
              "Developers tracking implementation",
              "QA teams managing releases",
              "Clients reviewing progress",
              "Startup teams building MVPs",
              "Agencies managing multiple products",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="
                  glass-card rounded-[22px] p-6
                "
              >
                <p className="text-[15px] font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ================= WHY IT MATTERS ================= */}
        <section className="mx-auto mt-32 max-w-5xl text-center">
          <h2 className="section-heading mb-6">
            Why Project Nova Matters
          </h2>

          <p className="section-copy">
            Nova doesn’t just help teams manage tasks — it preserves product
            knowledge. New team members can understand decisions instantly,
            stakeholders stay aligned, and teams avoid repeating mistakes.
          </p>

          <p className="section-copy mt-6">
            By storing the complete development journey, Project Nova becomes
            a living archive of how a product evolved over time.
          </p>
        </section>

        {/* ================= CTA ================= */}
        <section className="mt-32 text-center">
          <h2 className="section-heading mb-6">
            Build Products With Clarity
          </h2>

          <p className="mx-auto mb-8 max-w-xl text-center text-neutral-600">
            Bring planning, collaboration, and execution into one shared space.
          </p>

          <a
            href="/contact"
            className="
              inline-block px-10 py-3 rounded-full
              bg-black text-white font-medium
              hover:bg-opacity-80 transition
            "
          >
            Join the Waitlist
          </a>
        </section>

      </div>
    </div>
  );
}
